import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { arrayFromRange } from 'common/helper/array/from-range';
import { DataStore } from 'common/module/data/store';
import { GalleryBrowseEnum } from 'common/module/gallery/browse.enum';
import { GalleryDraggingStatusEnum } from 'common/module/gallery/dragging-status.enum';
import { GalleryTemplatePropsInterface } from 'common/module/gallery/template-props.interface';
import { GalleryViewStoreEvent } from 'common/module/gallery/view-store.event';
import { GalleryViewStoreOptionsInterface } from 'common/module/gallery/view-store.options.interface';

import { GalleryDirectionEnum } from './direction.enum';

export class GalleryViewStore extends DataStore<GalleryTemplatePropsInterface> {
  /**
   * Minimal number of pixels to drag to trigger an item change
   */
  public static readonly DRAG_DELTA_MIN: number = 10;

  /**
   * Custom on click handler
   */
  private onClickHandler: (e: MouseEvent) => void;

  /**
   * Custom on index change handler
   */
  private onIndexChangeHandler: (index: number) => void;

  /**
   * Initial touch registered
   */
  private initialTouch: TouchEvent;

  /**
   * Touch move direction
   */
  private touchDirection: GalleryDirectionEnum;

  /**
   * Carousel element
   */
  private gallery: HTMLElement;

  /**
   * flag for tealium
   */
  private isNavigationStarted: boolean = false;

  /**
   * Constructor
   */
  constructor(eventEmitter: EventEmitterInterface) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initState(): void {
    this.state = {
      isRtl: false,
      draggingStatus: GalleryDraggingStatusEnum.inactive,
      currentIndex: 0,
      itemsCount: 0,
      slidingIndex: [],
      visitedIndexes: {},
      itemsPositions: {},
      pointerPosition: {
        start: 0,
        current: 0,
      },
      browse: GalleryBrowseEnum.none,
      onClick: this.onClick,
      onTouchStart: this.onTouchStart,
      onMouseDown: this.onMouseDown,
      onGalleryRef: this.onGalleryRef,
    };
  }

  /**
   * @inheritDoc
   */
  public initialize(options: GalleryViewStoreOptionsInterface): void {
    if (isNaN(options.itemsCount) || options.itemsCount < 1) {
      return;
    }

    this.initState();

    this.state.isRtl = options.isRtl || false;
    this.state.itemsCount = options.itemsCount;
    this.state.itemsPositions = arrayFromRange(options.itemsCount).reduce(
      (obj, index) => ({
        ...obj,
        [index]: {
          positionX: 100,
        },
      }),
      {}
    );

    // Store custom click handler
    this.onClickHandler = options.onClick || null;
    // Store custom gallary index change handler
    this.onIndexChangeHandler = options.onIndexChange || null;

    this.setCurrentIndex(options.currentIndex || 0, !!options?.singleImagePreview, true);
  }

  /**
   * Attach listeners
   */
  public observers(): void {
    this.gallery.addEventListener(DomElementEvent.mousemove, this.onMouseMove);
    this.gallery.addEventListener(DomElementEvent.touchmove, this.onTouchMove, { passive: false });
    this.gallery.addEventListener(DomElementEvent.mouseup, this.onMouseUp);
    this.gallery.addEventListener(DomElementEvent.touchend, this.onTouchEnd);
  }

  /**
   * @inheritDoc
   */
  public destroy(): void {
    this.gallery.removeEventListener(DomElementEvent.mousemove, this.onMouseMove);
    this.gallery.removeEventListener(DomElementEvent.touchmove, this.onTouchMove);
    this.gallery.removeEventListener(DomElementEvent.mouseup, this.onMouseUp);
    this.gallery.removeEventListener(DomElementEvent.touchend, this.onTouchEnd);
  }

  /**
   * Set the current index
   */
  public setCurrentIndex(index: number, isSingleImagePreview?: boolean, isInitial: boolean = false): void {
    // Define browsing type
    let browse = GalleryBrowseEnum.none;

    if (index !== this.state.currentIndex) {
      browse = index > this.state.currentIndex ? GalleryBrowseEnum.next : GalleryBrowseEnum.previous;
    }

    // Handles any negative & positive index number
    const currentIndex = ((index % this.state.itemsCount) + this.state.itemsCount) % this.state.itemsCount;

    // Define the 3 sliding indexes
    const slidingIndex: number[] = isSingleImagePreview
      ? [currentIndex]
      : [
          (currentIndex - 1 + this.state.itemsCount) % this.state.itemsCount,
          currentIndex,
          (currentIndex + 1) % this.state.itemsCount,
        ];

    // Update sliding items position X
    const itemsPositions = this.state.itemsPositions;
    slidingIndex.forEach((itemIndex, i) => {
      itemsPositions[itemIndex].positionX = isSingleImagePreview ? 0 : (-100 + 100 * i) * (this.state.isRtl ? -1 : 1);
      this.state.visitedIndexes[itemIndex] = true;
    });

    // Update state
    this.setState(
      {
        ...this.getState(),
        itemsPositions,
        currentIndex,
        slidingIndex,
        browse,
      },
      this.setCurrentIndex
    );
    if (this.onIndexChangeHandler && !isInitial) {
      this.onIndexChangeHandler(currentIndex);
    }

    // Emit event
    this.getEventEmitter().emit(GalleryViewStoreEvent.browse, currentIndex);
  }

  /**
   * Returns pointer position delta
   */
  protected getPositionDelta(): number {
    return this.state.pointerPosition.current - this.state.pointerPosition.start;
  }

  /**
   * User control down
   */
  protected controlDown(positionX: number): void {
    // Keep original mouse position
    this.setPointerPositionStart(positionX);
    this.setPointerPositionCurrent(positionX);

    // Update drag status
    this.setDraggingStatus(GalleryDraggingStatusEnum.pending);
  }

  /**
   * User control up
   */
  protected controlUp(positionX: number): void {
    // Update pointer position
    this.setPointerPositionCurrent(positionX);

    const state = this.getState();
    const positionDelta = this.getPositionDelta();

    // Not dragging or minimal delta distance not reached
    if (
      this.getState().draggingStatus !== GalleryDraggingStatusEnum.active ||
      Math.abs(positionDelta) < GalleryViewStore.DRAG_DELTA_MIN
    ) {
      // Update drag status
      this.setDraggingStatus(GalleryDraggingStatusEnum.inactive);

      return;
    }

    const indexDelta = (state.isRtl ? -1 : 1) * (positionDelta > 0 ? -1 : 1);
    // Determine the direction of the drag and increment or decrement slide
    this.setCurrentIndex(state.currentIndex + indexDelta);

    // Update drag status
    this.setDraggingStatus(GalleryDraggingStatusEnum.finished);

    // Emit event
    this.getEventEmitter().emit(indexDelta > 0 ? GalleryViewStoreEvent.next : GalleryViewStoreEvent.previous);
  }

  /**
   * User control move
   */
  protected controlMove(positionX: number): void {
    // Pending dragging
    if (this.getState().draggingStatus === GalleryDraggingStatusEnum.pending) {
      // Start dragging
      this.setDraggingStatus(GalleryDraggingStatusEnum.active);
    }

    // No active dragging
    if (this.getState().draggingStatus !== GalleryDraggingStatusEnum.active) {
      return;
    }

    // Keep current mouse position
    this.setPointerPositionCurrent(positionX);
  }

  /**
   * Set dragging status
   */
  private setDraggingStatus(draggingStatus: GalleryDraggingStatusEnum): void {
    this.setState(
      {
        ...this.getState(),
        draggingStatus,
      },
      this.setDraggingStatus
    );
  }

  /**
   * Update the position of the pointer
   */
  private setPointerPositionCurrent(position: number): void {
    const state = this.getState();

    state.pointerPosition.current = position;

    this.setState(state, this.setPointerPositionCurrent);
  }

  /**
   * Update the start position of the pointer
   */
  private setPointerPositionStart(position: number): void {
    const state = this.getState();

    state.pointerPosition.start = position;

    this.setState(state, this.setPointerPositionStart);
  }

  /**
   * Clicked the next button
   */
  private onClick = (e: MouseEvent): void => {
    // Perform custom handler on click
    if (this.onClickHandler) {
      return this.onClickHandler(e);
    }

    // Dragging is not inactive
    if (this.getState().draggingStatus !== GalleryDraggingStatusEnum.inactive) {
      return;
    }

    // Gallery size
    const size = (<HTMLElement>e.currentTarget).getBoundingClientRect();

    const isDraggingItemForward = (e.offsetX / size.width) * 100 >= 50;

    // Reverse new index if isRtl
    const reverseValue = this.state.isRtl ? -1 : 1;

    const newIndex = (isDraggingItemForward ? 1 : -1) * reverseValue;

    const currentIndex = this.getState().currentIndex + newIndex;

    // Update slide
    this.setCurrentIndex(currentIndex);

    // Emit event
    this.getEventEmitter().emit(newIndex > 0 ? GalleryViewStoreEvent.next : GalleryViewStoreEvent.previous);
  };

  /**
   *
   * @description calculates direction of touch move
   */
  private getTouchDirection(firstTouchMoveEvent: TouchEvent): GalleryDirectionEnum {
    const start = this.initialTouch.touches[0];
    const finish = firstTouchMoveEvent.touches[0];

    const xDiff = Math.abs(start.clientX - finish.clientX);
    const yDiff = Math.abs(start.clientY - finish.clientY);

    return xDiff > yDiff ? GalleryDirectionEnum.HORIZONTAL : GalleryDirectionEnum.VERTICAL;
  }

  /**
   * Touch started
   */
  private onTouchStart = (e: TouchEvent): void => {
    this.initialTouch = e;
    if (!this.isNavigationStarted) {
      this.initiateNavigation();
    }
    this.controlDown(e.touches[0].pageX);
  };

  /**
   * Touch moved on document
   */
  private onTouchMove = (e: TouchEvent): void => {
    const draggingStatus = this.getState().draggingStatus;

    if (draggingStatus !== GalleryDraggingStatusEnum.pending && draggingStatus !== GalleryDraggingStatusEnum.active) {
      return;
    }
    const direction: GalleryDirectionEnum = this.touchDirection ? this.touchDirection : this.getTouchDirection(e);
    if (!this.touchDirection) {
      this.touchDirection = direction;
    }

    if (direction === GalleryDirectionEnum.HORIZONTAL) {
      this.controlMove(e.changedTouches[0].pageX);

      if (Math.abs(this.getPositionDelta()) >= GalleryViewStore.DRAG_DELTA_MIN / 2 && e.cancelable) {
        // Prevent from drag n drop
        e.preventDefault();
      }
    }
  };

  /**
   * Touch ended
   */
  private onTouchEnd = (e: TouchEvent): void => {
    this.controlUp(e.changedTouches[0].pageX);
    this.touchDirection = null;
  };

  /**
   * Pressing down mouse button
   */
  private onMouseDown = (e: MouseEvent): void => {
    // Don't want to be drag'n'droping images, do we ?
    e.preventDefault();
    if (!this.isNavigationStarted) {
      this.initiateNavigation();
    }
    this.controlDown(e.clientX);
  };

  /**
   * Moved mouse
   */
  private onMouseMove = (e: MouseEvent): void => {
    const draggingStatus = this.getState().draggingStatus;

    if (draggingStatus !== GalleryDraggingStatusEnum.pending && draggingStatus !== GalleryDraggingStatusEnum.active) {
      return;
    }

    // Don't want to be drag'n'droping images, do we ?
    e.preventDefault();

    this.controlMove(e.clientX);
  };

  /**
   * Released mouse button
   */
  private onMouseUp = (e: MouseEvent): void => {
    this.controlUp(e.clientX);
  };

  /**
   * Reference of carousel
   */
  private onGalleryRef = (ref: HTMLElement) => {
    if (ref) {
      this.gallery = ref;
      // Attach listeners
      this.observers();
    }
  };

  /**
   * fires event when navigation is started
   */
  private initiateNavigation = (): void => {
    this.getEventEmitter().emit(GalleryViewStoreEvent.start);
    this.isNavigationStarted = true;
  };
}
